// Size Chart Unit Converter
class SizeChartConverter {
    constructor() {
        this.init();
    }

    init() {
        document.addEventListener('DOMContentLoaded', () => {
            this.setupConvertButtons();
        });
    }

    setupConvertButtons() {
        const convertButtons = document.querySelectorAll('.size-chart-convert-btn');
        convertButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleConversion(button);
            });
        });
    }

    handleConversion(button) {
        const container = button.closest('.size-chart-container');
        const table = container.querySelector('.size-chart-table');
        const currentUnit = button.getAttribute('data-current-unit');
        const convertText = button.getAttribute('data-convert-text');
        const convertBackText = button.getAttribute('data-convert-back-text');

        if (currentUnit === 'imperial') {
            this.convertToMetric(table);
            button.textContent = convertBackText;
            button.setAttribute('data-current-unit', 'metric');
        } else {
            this.convertToImperial(table);
            button.textContent = convertText;
            button.setAttribute('data-current-unit', 'imperial');
        }
    }

    convertToMetric(table) {
        const cells = table.querySelectorAll('td[data-original-value]');
        cells.forEach(cell => {
            const originalValue = cell.getAttribute('data-original-value');
            const columnIndex = parseInt(cell.getAttribute('data-column-index'));
            
            // 跳过第一列（通常是尺码列）
            if (columnIndex === 0) {
                return;
            }
            
            const convertedValue = this.convertValue(originalValue, 'toMetric');
            cell.textContent = convertedValue;
        });

        // Update headers
        const headers = table.querySelectorAll('th[data-header]');
        headers.forEach((header, index) => {
            // 跳过第一列的表头
            if (index === 0) {
                return;
            }
            
            const originalHeader = header.getAttribute('data-header');
            const convertedHeader = this.convertHeader(originalHeader, 'toMetric');
            header.textContent = convertedHeader;
        });
    }

    convertToImperial(table) {
        const cells = table.querySelectorAll('td[data-original-value]');
        cells.forEach(cell => {
            const columnIndex = parseInt(cell.getAttribute('data-column-index'));
            
            // 跳过第一列（通常是尺码列）
            if (columnIndex === 0) {
                return;
            }
            
            const originalValue = cell.getAttribute('data-original-value');
            cell.textContent = originalValue;
        });

        // Restore original headers
        const headers = table.querySelectorAll('th[data-header]');
        headers.forEach((header, index) => {
            // 跳过第一列的表头
            if (index === 0) {
                return;
            }
            
            const originalHeader = header.getAttribute('data-header');
            header.textContent = originalHeader;
        });
    }

    convertValue(value, direction) {
        // Handle ranges like "20.9-21.3"
        if (value.includes('-')) {
            const [min, max] = value.split('-');
            const convertedMin = this.convertSingleValue(min.trim(), direction);
            const convertedMax = this.convertSingleValue(max.trim(), direction);
            return `${convertedMin}-${convertedMax}`;
        }
        
        return this.convertSingleValue(value, direction);
    }

    convertSingleValue(value, direction) {
        // 不转换尺码名称（如 XS, SM, MD, LG, XL 等）
        if (this.isSizeName(value)) {
            return value;
        }

        // Extract number from string (remove units)
        const numMatch = value.match(/[\d.]+/);
        if (!numMatch) return value;
        
        const num = parseFloat(numMatch[0]);
        let converted;

        if (direction === 'toMetric') {
            // Check if it's likely inches (common range 10-30) or pounds (common range 1-10)
            if (this.isLikelyInches(value, num)) {
                converted = (num * 2.54).toFixed(1);
                return converted; // 不添加单位标志
            } else if (this.isLikelyPounds(value, num)) {
                converted = (num * 0.453592).toFixed(1);
                return converted; // 不添加单位标志
            } else if (this.isLikelyFahrenheit(value, num)) {
                converted = ((num - 32) * 5/9).toFixed(0);
                return converted; // 不添加单位标志
            }
        }

        return value; // Return original if can't convert
    }

    convertHeader(header, direction) {
        const lowerHeader = header.toLowerCase();
        
        if (direction === 'toMetric') {
            if (lowerHeader.includes('(in)') || lowerHeader.includes('inch')) {
                return header.replace(/\(in\)/gi, '(CM)').replace(/inch/gi, 'CM');
            } else if (lowerHeader.includes('(lb)') || lowerHeader.includes('pound')) {
                return header.replace(/\(lb\)/gi, '(KG)').replace(/pound/gi, 'KG');
            } else if (lowerHeader.includes('(f)') || lowerHeader.includes('fahrenheit')) {
                return header.replace(/\(f\)/gi, '(C)').replace(/fahrenheit/gi, 'Celsius');
            }
        }

        return header;
    }

    isLikelyInches(value, num) {
        const lowerValue = value.toLowerCase();
        return (
            lowerValue.includes('in') ||
            lowerValue.includes('"') ||
            (num >= 10 && num <= 50) // Common inch range for clothing/gear
        );
    }

    isLikelyPounds(value, num) {
        const lowerValue = value.toLowerCase();
        return (
            lowerValue.includes('lb') ||
            lowerValue.includes('pound') ||
            (num >= 0.5 && num <= 20) // Common pound range for gear
        );
    }

    isLikelyFahrenheit(value, num) {
        const lowerValue = value.toLowerCase();
        return (
            lowerValue.includes('f') ||
            lowerValue.includes('fahrenheit') ||
            lowerValue.includes('°f') ||
            (num >= -40 && num <= 120) // Common Fahrenheit range
        );
    }

    isSizeName(value) {
        // 检查是否是常见的尺码名称
        const trimmedValue = value.trim().toUpperCase();
        const commonSizes = [
            'XXS', 'XS', 'S', 'SM', 'M', 'MD', 'L', 'LG', 'XL', 'XXL', 'XXXL',
            '2XS', '3XS', '2XL', '3XL', '4XL', '5XL',
            'SMALL', 'MEDIUM', 'LARGE', 'EXTRA SMALL', 'EXTRA LARGE'
        ];
        
        // 检查是否是数字尺码（如 32, 34, 36 等）但排除小数
        if (/^\d+$/.test(trimmedValue) && trimmedValue.length <= 3) {
            return true;
        }
        
        // 检查是否在常见尺码列表中
        return commonSizes.includes(trimmedValue);
    }
}

// Initialize the converter
new SizeChartConverter(); 